"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/lib/format";
import {
  Banknote,
  CreditCard,
  Megaphone,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Crown,
  Mail,
  Loader2,
} from "lucide-react";
import type { RevenueMix, LaunchPartnerAlert } from "@/lib/types";

// ============================================================
// V1.0 Monetization — Module 3: Admin "Revenue Mix" Dashboard
// ------------------------------------------------------------
// Shows the breakdown of revenue by source (Commission /
// Subscriptions / Advertising) with percentages + MoM growth.
// Includes the "5% Launch Partners" alert with renewal CTA.
// ============================================================

const REVENUE_SOURCES = [
  {
    key: "commission",
    labelKey: "admin.dashboard.commission",
    icon: Banknote,
    color: "text-emerald-600",
    bg: "bg-emerald-500",
    descriptionKey: "admin.revenueMix.commissionDesc",
  },
  {
    key: "subscriptions",
    labelKey: "nav.subscriptions",
    icon: CreditCard,
    color: "text-primary",
    bg: "bg-primary",
    descriptionKey: "admin.revenueMix.subscriptionsDesc",
  },
  {
    key: "advertising",
    labelKey: "nav.advertising",
    icon: Megaphone,
    color: "text-amber-600",
    bg: "bg-amber-500",
    descriptionKey: "admin.revenueMix.advertisingDesc",
  },
] as const;

export function RevenueMixCard({ range = "30d" }: { range?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "analytics", range],
    queryFn: () => api.admin.analytics({ range }),
    refetchInterval: 30000,
  });

  const analytics = data?.data?.analytics;
  const revenueMix: RevenueMix | undefined = analytics?.revenueMix;
  const launchPartnerAlerts: LaunchPartnerAlert[] = analytics?.launchPartnerAlerts ?? [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("admin.revenueMix.loading")}
        </CardContent>
      </Card>
    );
  }

  if (!revenueMix) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          {t("admin.revenueMix.unavailable")}
        </CardContent>
      </Card>
    );
  }

  const sources = REVENUE_SOURCES.map((s) => ({
    ...s,
    label: t(s.labelKey),
    description: t(s.descriptionKey),
    amount:
      s.key === "commission"
        ? revenueMix.commission
        : s.key === "subscriptions"
        ? revenueMix.subscriptions
        : revenueMix.advertising,
    pct:
      s.key === "commission"
        ? revenueMix.commissionPct
        : s.key === "subscriptions"
        ? revenueMix.subscriptionsPct
        : revenueMix.advertisingPct,
  }));

  const momPositive = revenueMix.momGrowthPct >= 0;

  const sendRenewalReminders = async () => {
    // Best-effort: log a notice. In a real system this would trigger an
    // email job per launch partner. For now we just toast + invalidate.
    toast.success(
      t("admin.revenueMix.renewalRemindersQueued", { count: launchPartnerAlerts.length })
    );
    qc.invalidateQueries({ queryKey: ["admin", "analytics", range] });
  };

  return (
    <div className="space-y-3">
      {/* Revenue breakdown card */}
      <Card className="overflow-hidden">
        <CardContent className="p-4 sm:p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Banknote className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-wide">
                  {t("admin.revenueMix.title")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {range === "7d" ? t("admin.revenueMix.range7d") : range === "90d" ? t("admin.revenueMix.range90d") : t("admin.revenueMix.range30d")}
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className={
                momPositive
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  : "border-red-300 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
              }
            >
              {momPositive ? (
                <TrendingUp className="mr-1 h-3 w-3" />
              ) : (
                <TrendingDown className="mr-1 h-3 w-3" />
              )}
              {momPositive ? "+" : ""}
              {revenueMix.momGrowthPct.toFixed(1)}% {t("admin.revenueMix.momSuffix")}
            </Badge>
          </div>

          {/* Stacked bar visualization */}
          <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full bg-muted">
            {sources.map((s) => (
              <div
                key={s.key}
                className={`${s.bg} transition-all`}
                style={{ width: `${s.pct}%` }}
                title={`${s.label}: ${formatPrice(s.amount)} (${s.pct.toFixed(1)}%)`}
              />
            ))}
          </div>

          {/* Source rows */}
          <div className="mt-4 space-y-2">
            {sources.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className={`flex h-7 w-7 items-center justify-center rounded-md ${s.bg}/15`}>
                      <Icon className={`h-3.5 w-3.5 ${s.color}`} />
                    </div>
                    <div>
                      <p className="font-medium">{s.label}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {s.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{formatPrice(s.amount)}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {s.pct.toFixed(1)}%
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total */}
          <div className="mt-3 flex items-center justify-between border-t pt-3">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                {t("admin.revenueMix.totalRevenue")}
              </p>
              <p className="text-2xl font-extrabold">
                {formatPrice(revenueMix.total)}
              </p>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <p>{t("admin.revenueMix.prevPeriod")} {formatPrice(revenueMix.previousMonthTotal)}</p>
              <p>
                {momPositive ? "▲" : "▼"}{" "}
                {Math.abs(
                  revenueMix.total - revenueMix.previousMonthTotal
                ).toLocaleString(undefined, {
                  style: "currency",
                  currency: "USD",
                })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Launch Partner alerts */}
      <Card className={launchPartnerAlerts.length > 0 ? "border-amber-300" : ""}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                  launchPartnerAlerts.length > 0
                    ? "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <Crown className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-wide">
                  {t("admin.revenueMix.launchPartnersTitle")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {launchPartnerAlerts.length === 0
                    ? t("admin.revenueMix.noLaunchPartners")
                    : t("admin.revenueMix.launchPartnersCount", {
                        count: launchPartnerAlerts.length,
                        below: launchPartnerAlerts.filter((a) => a.belowMoq).length,
                      })}
                </p>
              </div>
            </div>
            {launchPartnerAlerts.length > 0 && (
              <Button size="sm" variant="outline" onClick={sendRenewalReminders}>
                <Mail className="mr-1 h-3.5 w-3.5" />
                {t("admin.revenueMix.sendReminders")}
              </Button>
            )}
          </div>

          {launchPartnerAlerts.length > 0 && (
            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.revenueMix.colPartner")}</TableHead>
                    <TableHead>{t("admin.revenueMix.colStore")}</TableHead>
                    <TableHead className="text-right">{t("admin.revenueMix.colDaysLeft")}</TableHead>
                    <TableHead className="text-right">{t("admin.revenueMix.col30DayOrders")}</TableHead>
                    <TableHead className="text-right">{t("common.status")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {launchPartnerAlerts.map((alert) => (
                    <TableRow key={alert.userId}>
                      <TableCell className="font-medium">{alert.ownerName}</TableCell>
                      <TableCell>{alert.storeName}</TableCell>
                      <TableCell className="text-right">
                        <span
                          className={
                            alert.daysUntilExpiry <= 30
                              ? "font-bold text-amber-600"
                              : ""
                          }
                        >
                          {alert.daysUntilExpiry}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {alert.monthlyOrderCount}/100
                      </TableCell>
                      <TableCell className="text-right">
                        {alert.belowMoq ? (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300">
                            <AlertTriangle className="mr-1 h-3 w-3" />
                            {t("admin.revenueMix.belowMoq")}
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300">
                            {t("admin.revenueMix.ok")}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="mt-2 text-[10px] text-muted-foreground">
                {t("admin.revenueMix.launchPartnerNote")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
